import { useCallback } from 'react';

import {
  useZoomAuditLogQuery,
  ZoomAuditLogQueryVariables,
  ZoomAuditEntryFieldsFragment,
} from 'core/graphql';

type ZoomAuditFilters = Omit<ZoomAuditLogQueryVariables, 'first' | 'after'>;

interface IZoomAuditLogHook {
  data: ZoomAuditEntryFieldsFragment[];
  loading: boolean;
  error?: string;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/** Relay-cursor paginated Zoom audit log. */
export const useZoomAuditLog = (
  filters: ZoomAuditFilters = {},
  pageSize = 50,
): IZoomAuditLogHook => {
  const { data, loading, error, fetchMore, refetch } = useZoomAuditLogQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.zoomAuditLog;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.zoomAuditLog) {
          return prev;
        }

        return {
          zoomAuditLog: {
            ...fetchMoreResult.zoomAuditLog,
            edges: [
              ...(prev.zoomAuditLog?.edges ?? []),
              ...fetchMoreResult.zoomAuditLog.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is ZoomAuditEntryFieldsFragment => Boolean(node)),
    loading,
    error: error?.message,
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
